/**
 * Internationalization (i18n) Configuration for Web Package
 * 
 * Sets up i18next for multi-language support in the Gantty web application.
 * Configures language detection, resource loading, and translation management.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { getCurrentLanguage } from '../utils/urlUtils';

// Import all translation resources
import enCommon from './locales/en/common.json';
import huCommon from './locales/hu/common.json';
import arCommon from './locales/ar/common.json';
import frCommon from './locales/fr/common.json';
import zhCNCommon from './locales/zh-cn/common.json';
import koCommon from './locales/ko/common.json';
import itCommon from './locales/it/common.json';
import esCommon from './locales/es/common.json';
import jaCommon from './locales/ja/common.json';
import ruCommon from './locales/ru/common.json';
import trCommon from './locales/tr/common.json';
import ptBRCommon from './locales/pt-br/common.json';

/**
 * Default namespace for translations.
 */
export const defaultNamespace = 'common';

/**
 * Translation resources for all supported languages
 */
export const resources = {
  en: {
    [defaultNamespace]: enCommon,
  },
  hu: {
    [defaultNamespace]: huCommon,
  },
  ar: {
    [defaultNamespace]: arCommon,
  },
  fr: {
    [defaultNamespace]: frCommon,
  },
  'zh-cn': {
    [defaultNamespace]: zhCNCommon,
  },
  ko: {
    [defaultNamespace]: koCommon,
  },
  it: {
    [defaultNamespace]: itCommon,
  },
  es: {
    [defaultNamespace]: esCommon,
  },
  ja: {
    [defaultNamespace]: jaCommon,
  },
  ru: {
    [defaultNamespace]: ruCommon,
  },
  tr: {
    [defaultNamespace]: trCommon,
  },
  'pt-br': {
    [defaultNamespace]: ptBRCommon,
  },
} as const;

/**
 * localStorage key for storing user's language preference.
 */
const LOCAL_STORAGE_KEY = 'gantty_language';

/**
 * Initialize i18next with React integration and language detection.
 * 
 * Configures i18next with all translation resources, language detection,
 * and React integration. Sets up development warnings for missing translations.
 */
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    ns: [defaultNamespace],
    defaultNS: defaultNamespace,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag', 'cookie'],
      caches: ['localStorage'],
      lookupLocalStorage: LOCAL_STORAGE_KEY,
      // Convert language codes to lowercase with dash (e.g., 'en-US' -> 'en-us')
      convertDetectedLanguage: (lng: string) => {
        return lng.toLowerCase().replace('_', '-');
      },
    },
    returnNull: false,
  });

// Set initial language from URL or detection
const initialLang = getCurrentLanguage();
i18n.changeLanguage(initialLang);

if (process.env.NODE_ENV !== 'production') {
  i18n.on('missingKey', (lngs, ns, key) => {
    // eslint-disable-next-line no-console
    console.warn(`[i18n] Missing translation for key "${key}" in locales: ${lngs?.join(', ')}`);
  });
}

export default i18n;

